import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Brain, Send, X, Minimize2, Maximize2, Loader, BarChart3, Table as TableIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIChatWidget = ({ standalone = false, onClose }) => {
  const [messages, setMessages] = useState([
    {
      type: 'ai',
      content: 'Hi! I\'m your AI-powered Virtual CFO Assistant. Ask me anything about financial analysis, risks, or opportunities.',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const parseAIResponse = (response) => {
    let content = response;
    let chartData = null;
    let tableData = null;
    let chartType = null;

    // Find chart using index-based parsing
    const chartStart = response.indexOf('[CHART:');
    if (chartStart !== -1) {
      const typeEnd = response.indexOf(':', chartStart + 7);
      if (typeEnd !== -1) {
        chartType = response.substring(chartStart + 7, typeEnd).trim();
        const jsonStart = typeEnd + 1;
        
        let bracketCount = 0;
        let jsonEnd = -1;
        for (let i = jsonStart; i < response.length; i++) {
          if (response[i] === '[') bracketCount++;
          else if (response[i] === ']') {
            bracketCount--;
            if (bracketCount === 0) {
              jsonEnd = i + 1;
              if (response[i + 1] === ']') break;
            }
          }
        }
        
        if (jsonEnd !== -1) {
          const jsonStr = response.substring(jsonStart, jsonEnd);
          try {
            chartData = JSON.parse(jsonStr);
            content = response.substring(0, chartStart) + response.substring(jsonEnd + 1);
          } catch (e) {
            console.error('Failed to parse chart:', e);
          }
        }
      }
    }

    // Find table
    const tableStart = content.indexOf('[TABLE:');
    if (tableStart !== -1) {
      const jsonStart = tableStart + 7;
      let braceCount = 0;
      let jsonEnd = -1;
      
      for (let i = jsonStart; i < content.length; i++) {
        if (content[i] === '{') braceCount++;
        else if (content[i] === '}') {
          braceCount--;
          if (braceCount === 0) {
            jsonEnd = i + 1;
            break;
          }
        }
      }
      
      if (jsonEnd !== -1) {
        const jsonStr = content.substring(jsonStart, jsonEnd);
        try {
          tableData = JSON.parse(jsonStr);
          content = content.substring(0, tableStart) + content.substring(jsonEnd + 1);
        } catch (e) {
          console.error('Failed to parse table:', e);
        }
      }
    }

    return { content: content.trim(), chartData, chartType, tableData };
  };

  const parseContentSections = (content) => {
    const sections = [];
    const lines = content.split('\n');
    let introText = '';
    let currentSection = null;
    
    lines.forEach(line => {
      if (line.match(/^\*\*[^*]+:\*\*/) || line.match(/^###/)) {
        if (currentSection) {
          sections.push(currentSection);
        }
        const title = line.replace(/^\*\*|\*\*:?$/g, '').replace(/^###\s*/, '').trim();
        currentSection = { title, content: '' };
      } else if (currentSection) {
        currentSection.content += line + '\n';
      } else {
        introText += line + '\n';
      }
    });
    
    if (currentSection) {
      sections.push(currentSection);
    }
    
    if (introText.trim() && sections.length > 0) {
      sections.unshift({ title: 'Summary', content: introText.trim() });
    }
    
    return sections.length > 0 ? sections : [{ title: '', content }];
  };

  const formatContent = (text) => {
    text = text.replace(/^- (.+)$/gm, '<li class="ml-4 mb-1">$1</li>');
    text = text.replace(/^• (.+)$/gm, '<li class="ml-4 mb-1">$1</li>');
    text = text.replace(/(<li[^>]*>.*<\/li>\s*)+/gs, (match) => `<ul class="list-disc space-y-1 my-2">${match}</ul>`);
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>');
    text = text.replace(/(₺[\d,]+)/g, '<span class="font-semibold text-blue-600">$1</span>');
    text = text.replace(/(\d+%)/g, '<span class="font-semibold text-purple-600">$1</span>');
    text = text.replace(/\n\n/g, '<br/><br/>');
    text = text.replace(/\n/g, '<br/>');
    return text;
  };

  const renderChart = (data, type) => {
    const COLORS = ['#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'];

    if (type === 'bar') {
      return (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="value" fill="#0ea5e9" />
          </BarChart>
        </ResponsiveContainer>
      );
    } else if (type === 'line') {
      return (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      );
    } else if (type === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={60}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    return null;
  };

  const renderTable = (data) => {
    if (!data || !data.headers || !data.rows) return null;

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-300 text-xs">
          <thead className="bg-gray-100">
            <tr>
              {data.headers.map((header, idx) => (
                <th key={idx} className="border border-gray-300 px-2 py-1 text-left font-semibold">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, rowIdx) => (
              <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx} className="border border-gray-300 px-2 py-1">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const handleSend = async (query = input) => {
    if (!query.trim()) return;

    const userMessage = {
      type: 'user',
      content: query,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const response = await axios.post(`${API}/ai/chat`, {
        message: query,
        session_id: sessionId
      });

      const parsed = parseAIResponse(response.data.response);

      setMessages(prev => [...prev, {
        type: 'ai',
        content: parsed.content,
        chartData: parsed.chartData,
        chartType: parsed.chartType,
        tableData: parsed.tableData,
        timestamp: new Date()
      }]);

      if (!sessionId) {
        setSessionId(response.data.session_id);
      }
    } catch (error) {
      console.error('AI chat error:', error);
      setMessages(prev => [...prev, {
        type: 'ai',
        content: 'I apologize for the technical difficulty. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const containerClass = standalone 
    ? "w-full h-screen flex flex-col bg-gradient-to-b from-purple-50 to-white"
    : isMinimized 
      ? "hidden"
      : "flex flex-col bg-white rounded-lg shadow-2xl border border-gray-200 w-full max-w-2xl h-[600px]";

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-3 flex items-center justify-between rounded-t-lg">
        <div className="flex items-center gap-2">
          <Brain size={20} />
          <span className="font-semibold text-sm">AI CFO Assistant</span>
        </div>
        <div className="flex items-center gap-2">
          {!standalone && (
            <>
              <button onClick={() => setIsMinimized(!isMinimized)} className="hover:bg-white/20 p-1 rounded">
                <Minimize2 size={16} />
              </button>
              {onClose && (
                <button onClick={onClose} className="hover:bg-white/20 p-1 rounded">
                  <X size={16} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((message, index) => (
          <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
              {message.type === 'ai' && (
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center">
                    <Brain size={12} className="text-white" />
                  </div>
                  <span className="text-xs font-semibold text-gray-500">AI Assistant</span>
                </div>
              )}
              
              <div className={`rounded-xl px-3 py-2 text-sm ${
                message.type === 'user' 
                  ? 'bg-sky-500 text-white rounded-tr-sm' 
                  : 'bg-white border border-gray-200 rounded-tl-sm'
              }`}>
                {message.type === 'user' ? (
                  <p>{message.content}</p>
                ) : (
                  <>
                    {(() => {
                      const sections = parseContentSections(message.content);
                      
                      if (sections.length === 1 && !sections[0].title) {
                        return (
                          <div 
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: formatContent(message.content) }}
                          />
                        );
                      }
                      
                      return (
                        <div className="space-y-2">
                          {sections[0]?.title === 'Summary' && (
                            <div 
                              className="mb-3 pb-2 border-b prose prose-sm max-w-none"
                              dangerouslySetInnerHTML={{ __html: formatContent(sections[0].content) }}
                            />
                          )}
                          
                          <Accordion type="single" collapsible className="space-y-1">
                            {sections.filter(s => s.title !== 'Summary').map((section, idx) => (
                              <AccordionItem 
                                key={idx} 
                                value={`s-${idx}`}
                                className="border rounded-md px-2 py-1 bg-gray-50"
                              >
                                <AccordionTrigger className="text-xs font-semibold hover:no-underline py-2">
                                  <div className="flex items-center gap-2 text-left">
                                    <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                      {idx + 1}
                                    </span>
                                    <span>{section.title}</span>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="text-xs pt-1 pb-2">
                                  <div 
                                    className="prose prose-xs max-w-none"
                                    dangerouslySetInnerHTML={{ __html: formatContent(section.content) }}
                                  />
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </div>
                      );
                    })()}
                  </>
                )}
                
                {message.chartData && message.chartType && (
                  <div className="mt-3 p-2 bg-purple-50 rounded border border-purple-100">
                    <div className="flex items-center gap-1 mb-2">
                      <BarChart3 size={14} className="text-purple-600" />
                      <span className="text-xs font-semibold text-purple-900">Chart</span>
                    </div>
                    {renderChart(message.chartData, message.chartType)}
                  </div>
                )}

                {message.tableData && (
                  <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-100">
                    <div className="flex items-center gap-1 mb-2">
                      <TableIcon size={14} className="text-blue-600" />
                      <span className="text-xs font-semibold text-blue-900">Data</span>
                    </div>
                    {renderTable(message.tableData)}
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-400 mt-1">
                {message.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white border rounded-xl px-3 py-2">
              <div className="flex items-center gap-2">
                <Loader className="w-4 h-4 text-purple-500 animate-spin" />
                <span className="text-xs text-gray-500">Analyzing...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-3 bg-white rounded-b-lg">
        <div className="flex items-center gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !isTyping && handleSend()}
            placeholder="Ask about finances..."
            className="flex-1 text-sm"
            disabled={isTyping}
          />
          <Button
            onClick={() => handleSend()}
            disabled={!input.trim() || isTyping}
            size="sm"
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Send size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIChatWidget;
